import React, { useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Collapse from '@mui/material/Collapse';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import ExpandLessRoundedIcon from '@mui/icons-material/ExpandLessRounded';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';

import { shape } from 'prop-types';
import * as R from 'ramda';
import { format } from 'date-fns';
import { formatNumberWithoutDecimals } from 'utils/helpers/format';

const stackStyle = { borderRadius: 0.5, boxShadow: 6, p: 2, width: 150 };
const defaultToEmpty = R.defaultTo('--');

const Averages = ({ inventoryProjections }) => {
  const lgUp = useMediaQuery(theme => theme.breakpoints.up('lg'), { noSsr: true });
  const [collapseOpen, setCollapseOpen] = useState(true);

  const renderChip = status => {
    const date = format(new Date(), 'd');
    const isOpen = R.equals('OPEN', status);
    const isSuspendedEarly = R.and(R.equals('SUSPENDED', status), R.gte(7, parseInt(date)));
    return (
      <Chip
        label={status}
        sx={{
          '& > span': {
            color: isOpen || isSuspendedEarly ? 'text.primary' : 'surface.white',
          },
          backgroundColor: isOpen ? 'blue.light' : isSuspendedEarly ? 'warning.main' : 'error.main',
          height: 20,
        }}
      />
    );
  };

  const Projections = () => (
    <>
      <Typography variant="caption">
        PROJECTIONS BASED ON AVERAGE OF LAST 30 DAYS LOADING
      </Typography>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gridGap: 8 }}>
        <Stack spacing={0.5} sx={stackStyle}>
          <Typography color="text.primary" variant="caption">
            Avg. Daily Loadings
          </Typography>
          <Typography variant="caption">
            {formatNumberWithoutDecimals(
              defaultToEmpty(
                R.path(['thirtyDaysLoading', 'averageDailyLoadings'], inventoryProjections)
              )
            )}{' '}
            bbl
          </Typography>
        </Stack>
        <Stack spacing={0.5} sx={stackStyle}>
          <Typography color="text.primary" variant="caption">
            Current Supply
          </Typography>
          <Typography variant="caption">
            {defaultToEmpty(R.path(['thirtyDaysLoading', 'currentSupply'], inventoryProjections))}{' '}
            days
          </Typography>
        </Stack>
        <Stack spacing={0.5} sx={stackStyle}>
          <Typography color="text.primary" variant="caption">
            Estimated Min.
          </Typography>
          <Typography variant="caption">
            {formatNumberWithoutDecimals(
              defaultToEmpty(R.path(['thirtyDaysLoading', 'midNightMin'], inventoryProjections))
            )}{' '}
            bbl
          </Typography>
        </Stack>
        <Stack spacing={0.5} sx={stackStyle}>
          <Typography color="text.primary" variant="caption">
            Next Month Min.
          </Typography>
          <Typography variant="caption">
            {formatNumberWithoutDecimals(
              defaultToEmpty(
                R.path(['thirtyDaysLoading', 'nextMonthMinimum'], inventoryProjections)
              )
            )}{' '}
            bbl
          </Typography>
        </Stack>
        <Stack spacing={0.5} sx={stackStyle}>
          <Typography color="text.primary" variant="caption">
            Next Monday Max.
          </Typography>
          <Typography variant="caption">
            {formatNumberWithoutDecimals(
              defaultToEmpty(R.path(['thirtyDaysLoading', 'nextMondayMax'], inventoryProjections))
            )}{' '}
            bbl
          </Typography>
        </Stack>
        <Stack spacing={0.5} sx={{ ...stackStyle, whiteSpace: 'nowrap' }}>
          <Typography color="text.primary" variant="caption">
            Min/Max Participation
          </Typography>
          <Typography variant="caption">
            {defaultToEmpty(
              R.path(['thirtyDaysLoading', 'inventoryOwnerMaxParticipation'], inventoryProjections)
            )}
          </Typography>
        </Stack>
        <Stack spacing={0.5} sx={stackStyle}>
          <Typography color="text.primary" variant="caption">
            Min. System Vol.
          </Typography>
          <Typography variant="caption">
            {formatNumberWithoutDecimals(
              defaultToEmpty(
                R.path(['thirtyDaysLoading', 'minSystemVolumeTotal'], inventoryProjections)
              )
            )}{' '}
            bbl
          </Typography>
        </Stack>
        <Stack spacing={0.5} sx={stackStyle}>
          <Typography color="text.primary" variant="caption">
            Min. System Status
          </Typography>
          <Typography variant="caption">
            {renderChip(R.path(['thirtyDaysLoading', 'minSystemStatus'], inventoryProjections))}
          </Typography>
        </Stack>
      </Box>
    </>
  );

  return (
    <Stack spacing={lgUp ? 3 : 2} sx={{ mt: collapseOpen ? 3 : 1 }}>
      {lgUp ? (
        <Projections />
      ) : (
        <>
          <Collapse in={collapseOpen} timeout="auto" unmountOnExit>
            <Stack alignItems="center" direction="column" spacing={2}>
              <Projections />
            </Stack>
          </Collapse>
          <Button
            onClick={() => setCollapseOpen(!collapseOpen)}
            sx={{
              backgroundColor: 'transparent',
              display: 'inline-flex',
              justifyContent: 'space-between',
              mt: collapseOpen ? 2 : 0,
              px: 0,
              py: 1,
              width: 160,
            }}
            variant="text"
          >
            {collapseOpen ? <ExpandLessRoundedIcon /> : <ExpandMoreRoundedIcon />}
            {collapseOpen ? 'Collapse 30 Day Avg.' : 'Expand 30 Day Avg.'}
          </Button>
        </>
      )}
    </Stack>
  );
};

Averages.propTypes = {
  inventoryProjections: shape({}),
};

Averages.defaultProps = {
  inventoryProjections: null,
};

export default Averages;
